import {
  DialogActions,
  DialogContent,
  DialogContentProps,
  DialogTitle,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { useCallback } from 'react';

import { FONT_WEIGHTS } from '@/styles/tokens/fonts';

import { Button, ButtonProps } from '../../form/baseInputs/Button';
import { Dialog } from '../Dialog';

export type ConfirmationModalProps = React.PropsWithChildren<{
  isOpen?: boolean;
  heading: string;
  cancelText?: string;
  confirmText?: string;
  confirmButtonProps?: Partial<ButtonProps>;
  cancelButtonProps?: Partial<ButtonProps>;
  onClose: (reason: 'confirm' | 'cancel') => void;
  confirmationOnly?: boolean;
  dialogContentProps?: DialogContentProps;
  closeOnOutsideClick?: boolean;
  /** Override the Dialog's sx prop (e.g. for z-index) */
  sx?: SxProps;
}>;

export function ConfirmationModal({
  isOpen = false,
  heading,
  children,
  cancelText = 'Cancel',
  confirmText = 'Confirm',
  confirmButtonProps,
  cancelButtonProps,
  onClose,
  confirmationOnly = false,
  dialogContentProps,
  closeOnOutsideClick = true,
  sx,
}: ConfirmationModalProps) {
  const handleCancel = useCallback(() => {
    onClose('cancel');
  }, [onClose]);
  return (
    <Dialog
      open={isOpen}
      onClose={closeOnOutsideClick ? handleCancel : undefined}
      PaperProps={{ sx: { width: 325, p: 2, borderRadius: '8px' } }}
      sx={sx}
    >
      <DialogTitle
        textAlign="center"
        variant="title-s"
        fontWeight={FONT_WEIGHTS.bold}
        sx={{
          pt: 0,
          px: 0,
          pb: 0.25,
        }}
      >
        {heading}
      </DialogTitle>
      <DialogContent
        {...dialogContentProps}
        sx={{
          textAlign: 'center',
          p: 0,
          ...dialogContentProps?.sx,
        }}
      >
        <Typography
          display="block"
          variant="body-xs"
          sx={{ pt: 0, pb: 2, px: 0 }}
        >
          {children}
        </Typography>
      </DialogContent>
      <DialogActions disableSpacing sx={{ p: 0 }}>
        <Stack direction="row" spacing={1} flexGrow={1}>
          {!confirmationOnly && (
            <Button
              onClick={handleCancel}
              size="sm"
              variant="secondary"
              fullWidth
              {...cancelButtonProps}
            >
              {cancelText}
            </Button>
          )}
          <Button
            onClick={() => onClose('confirm')}
            size="sm"
            variant="primary"
            fullWidth
            {...confirmButtonProps}
          >
            {confirmText}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
